import React from 'react';
import { Helmet } from 'react-helmet-async';

function Privacy() {
  return (
    <div className="page-wrapper">
      <Helmet>
        <title>Privacy Policy | Clariti AI</title>
        <meta
          name="description"
          content="Read the Clariti AI privacy policy, including how we collect, process, and protect personal data."
        />
        <link rel="canonical" href="https://www.clariti.ai/privacy" />
        <meta property="og:title" content="Privacy Policy | Clariti AI" />
        <meta
          property="og:description"
          content="Learn how Clariti AI handles personal data, privacy rights, and information security."
        />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://www.clariti.ai/privacy" />
        <meta name="twitter:title" content="Privacy Policy | Clariti AI" />
        <meta
          name="twitter:description"
          content="Read the Clariti AI privacy policy and your data protection rights."
        />
      </Helmet>
      {/* Hero */}
      <section className="about-hero">
        <div className="container">
          <h1>Privacy Policy</h1>
          <p>Privacy Policy for Clariti AI Limited and clariti.ai</p>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section className="section">
        <div className="container">
          <div className="privacy-content" style={{ maxWidth: '900px', margin: '0 auto' }}>
            <p>
              This is the privacy policy of Clariti AI Limited. In this document, 'we', 'our', or 'us' refer to Clariti AI Limited. We are company number 12237967 registered in England & Wales.
            </p>
            <p>
              Our registered office is at 167-169 Great Portland Street, London W1W 5PF, United Kingdom.
            </p>

            <h2>Introduction</h2>
            <p>
              This privacy policy covers all pages within www.clariti.ai site and does not cover client-specific microsites that are guided by their own contracts. A client specific micro-site is denoted by clients.clariti.ai or [client engagement name].clariti.ai
            </p>
            <p>
              This privacy policy aims to inform you about how we collect and process any information that we collect from you, or that you provide to us. It covers information that could identify you ('personal information') and information that could not. In the context of the law and this notice, 'process' means collect, store, transfer, use or otherwise act on information. It tells you about your privacy rights and how the law protects you.
            </p>
            <p>
              We are committed to protecting your privacy and the confidentiality of your personal information. Our policy is not just an exercise in complying with the law, but a continuation of our respect for you and your personal information.
            </p>
            <p>
              We undertake to preserve the confidentiality of all information you provide to us, and hope that you reciprocate.
            </p>
            <p>
              Our policy complies with the Data Protection Act 2018 (Act) accordingly incorporating the EU General Data Protection Regulation (GDPR).
            </p>
            <p>
              The law requires us to tell you about your rights and our obligations to you in regard to the processing and control of your personal data. We do this now, by requesting that you read the information provided at <a href="https://www.knowyourprivacyrights.org" target="_blank" rel="noopener noreferrer">https://www.knowyourprivacyrights.org</a>
            </p>
            <p>
              Except as set out below, we do not share, or sell, or disclose to a third party, any information collected through our website.
            </p>

            <h2>1. Data we process</h2>
            <p>
              We may collect, use, store and transfer different kinds of personal data about you. We have collated these into groups as follows:
            </p>
            <p>
              Your identity includes information such as first name, last name, title, date of birth, and other identifiers that you may have provided at some time.
            </p>
            <p>
              Your contact information includes information such as billing address, delivery address, email address, telephone numbers and any other information you have given to us for the purpose of communication or meeting.
            </p>
            <p>
              Technical data includes your internet protocol (IP) address, browser type and version, time zone setting and location, browser plug-in types and versions, operating system and platform and other technology on the devices you use to access this website.
            </p>
            <p>
              We may aggregate anonymous data such as statistical or demographic data for any purpose. Anonymous data is data that does not identify you as an individual. Aggregated data may be derived from your personal data but is not considered personal information in law because it does not reveal your identity.
            </p>
            <p>
              For example, we may aggregate profile data to assess interest in a product or service.
            </p>
            <p>
              However, if we combine or connect aggregated data with your personal information so that it can identify you in any way, we treat the combined data as personal information and it will be used in accordance with this privacy policy.
            </p>

            <h2>2. Special personal information</h2>
            <p>
              Special personal information is data about your race or ethnicity, religious or philosophical beliefs, sex life, sexual orientation, political opinions, trade union membership, information about your health and genetic and biometric data. It also includes information about criminal convictions and offences.
            </p>
            <p>
              We do not collect any special personal information about you.
            </p>

            <h2>3. If you do not provide personal information we need</h2>
            <p>
              Where we need to collect personal data by law, or under the terms of a contract we have with you, and you fail to provide that data when requested, we may not be able to perform that contract. In that case, we may have to stop providing a service to you. If so, we will notify you of this at the time.
            </p>

            <h2>4. The bases on which we process information about you</h2>
            <p>
              The law requires us to determine under which of six defined bases we process different categories of your personal information, and to notify you of the basis for each category.
            </p>
            <p>
              If a basis on which we process your personal information is no longer relevant then we shall immediately stop processing your data.
            </p>
            <p>
              If the basis changes then if required by law we shall notify you of the change and of any new basis under which we have determined that we can continue to process your information.
            </p>

            <h2>5. Information we process because we have a contractual obligation with you</h2>
            <p>
              When you create an account on our website, buy a product or service from us, or otherwise agree to our terms and conditions, a contract is formed between you and us.
            </p>
            <p>
              In order to carry out our obligations under that contract we must process the information you give us. Some of this information may be personal information.
            </p>
            <p>
              We may use it in order to:
            </p>
            <ul>
              <li>verify your identity for security purposes</li>
              <li>sell products to you</li>
              <li>provide you with our services</li>
              <li>provide you with suggestions and advice on products, services and how to obtain the most from using our website</li>
            </ul>
            <p>
              We process this information on the basis there is a contract between us, or that you have requested we use the information before we enter into a legal contract.
            </p>
            <p>
              We shall continue to process this information until the contract between us ends or is terminated by either party under the terms of the contract.
            </p>

            <h2>6. Information we process with your consent</h2>
            <p>
              Through certain actions when otherwise there is no contractual relationship between us, such as when you browse our website or ask us to provide you more information about our business, including job opportunities and our products and services, you provide your consent to us to process information that may be personal information.
            </p>
            <p>
              Wherever possible, we aim to obtain your explicit consent to process this information, for example, by asking you to agree to our use of cookies.
            </p>
            <p>
              If you have given us explicit permission to do so, we may from time to time pass your name and contact information to selected associates whom we consider may provide services or products you would find useful.
            </p>
            <p>
              We continue to process your information on this basis until you withdraw your consent or it can be reasonably assumed that your consent no longer exists.
            </p>
            <p>
              You may withdraw your consent at any time by instructing us at <a href="mailto:info@clariti.ai">info@clariti.ai</a>. However, if you do so, you may not be able to use our website or our services further.
            </p>

            <h2>7. Information we process for the purposes of legitimate interests</h2>
            <p>
              We may process information on the basis there is a legitimate interest, either to you or to us, of doing so.
            </p>
            <p>
              Where we process your information on this basis, we do after having given careful consideration to:
            </p>
            <ul>
              <li>whether the same objective could be achieved through other means</li>
              <li>whether processing (or not processing) might cause you harm</li>
              <li>whether you would expect us to process your data, and whether you would, in the round, consider it reasonable to do so</li>
            </ul>
            <p>
              For example, we may process your data on this basis for the purposes of:
            </p>
            <ul>
              <li>record-keeping for the proper and necessary administration of our business</li>
              <li>responding to unsolicited communication from you to which we believe you would expect a response</li>
              <li>protecting and asserting the legal rights of any party</li>
              <li>insuring against or obtaining professional advice that is required to manage business risk</li>
              <li>protecting your interests where we believe we have a duty to do so</li>
            </ul>

            <h2>8. Information we process because we have a legal obligation</h2>
            <p>
              Sometimes, we must process your information in order to comply with a statutory obligation.
            </p>
            <p>
              For example, we may be required to give information to legal authorities if they so request or if they have the proper authorisation such as a search warrant or court order.
            </p>
            <p>
              This may include your personal information.
            </p>

            <h2>9. Complaints regarding content on our website</h2>
            <p>
              If you complain about any of the content on our website, we shall investigate your complaint.
            </p>
            <p>
              If we feel it is justified or if we believe the law requires us to do so, we shall remove the content while we investigate.
            </p>
            <p>
              Free speech is a fundamental right, so we have to make a judgment as to whose right will be obstructed: yours, or that of the person who posted the content that offends you.
            </p>
            <p>
              If we think your complaint is vexatious or without any basis, we shall not correspond with you about it.
            </p>

            <h2>10. Job application and employment</h2>
            <p>
              If you send us information in connection with a job application, we may keep it for up to three years in case we decide to contact you at a later date.
            </p>
            <p>
              If we employ you, we collect information about you and your work from time to time throughout the period of your employment. This information will be used only for purposes directly relevant to your employment. After your employment has ended, we will keep your file for six years before destroying or deleting it.
            </p>

            <h2>11. Communicating with us</h2>
            <p>
              When you contact us, whether by telephone, through our website or by e-mail, we collect the data you have given to us in order to reply with the information you need.
            </p>
            <p>
              We record your request and our reply in order to increase the efficiency of our business.
            </p>
            <p>
              We keep personally identifiable information associated with your message, such as your name and email address so as to be able to track our communications with you to provide a high quality service.
            </p>

            <h2>12. Complaining</h2>
            <p>
              When we receive a complaint, we record all the information you have given to us.
            </p>
            <p>
              We use that information to resolve your complaint.
            </p>
            <p>
              If your complaint reasonably requires us to contact some other person, we may decide to give to that other person some of the information contained in your complaint. We do this as infrequently as possible, but it is a matter for our sole discretion as to whether we do give information, and if we do, what that information is.
            </p>
            <p>
              We may also compile statistics showing information obtained from this source to assess the level of service we provide, but not in a way that could identify you or any other person.
            </p>

            <h2>13. Affiliate and business partner information</h2>
            <p>
              This is information given to us by you in your capacity as an affiliate of us or as a business partner.
            </p>
            <p>
              It allows us to recognise visitors that you have referred to us, and to credit to you commission due for such referrals. It also includes information that allows us to transfer commission to you.
            </p>
            <p>
              The information is not used for any other purpose.
            </p>
            <p>
              We undertake to preserve the confidentiality of the information and of the terms of our relationship.
            </p>
            <p>
              We expect any affiliate or partner to agree to reciprocate this policy.
            </p>

            <h2>14. Cookies</h2>
            <p>
              Cookies are small text files that are placed on your computer's hard drive by your web browser when you visit any website. They allow information gathered on one web page to be stored until it is needed for use on another, allowing a website to provide you with a personalised experience and the website owner with statistics about how you use the website so that it can be improved.
            </p>
            <p>
              Some cookies may last for a defined period of time, such as one day or until you close your browser. Others last indefinitely.
            </p>
            <p>
              Your web browser should allow you to delete any you choose. It also should allow you to prevent or limit their use.
            </p>
            <p>
              Our website uses cookies. They are placed by software that operates on our servers.
            </p>
            <p>
              When you first visit our website, we ask you whether you wish us to use cookies. If you choose not to accept them, we shall not use them for your visit except to record that you have not consented to their use for any other purpose.
            </p>
            <p>
              If you choose not to use cookies or you prevent their use through your browser settings, you will not be able to use all the functionality of our website.
            </p>
            <p>
              We use cookies in the following ways:
            </p>
            <ul>
              <li>to track how you use our website</li>
              <li>to record whether you have seen specific messages we display on our website</li>
              <li>to keep you signed in our site</li>
              <li>to record your answers to surveys and questionnaires on our site while you complete them</li>
              <li>to record the conversation thread during a live chat with our support team</li>
            </ul>

            <h2>15. Personal identifiers from your browsing activity</h2>
            <p>
              Requests by your web browser to our servers for web pages and other content on our website are recorded.
            </p>
            <p>
              We record information such as your geographical location, your Internet service provider and your IP address. We also record information about the software you are using to browse our website, such as the type of computer or device and the screen resolution.
            </p>
            <p>
              We use this information in aggregate to assess the popularity of the webpages on our website and how we perform in providing content to you.
            </p>
            <p>
              If combined with other information we know about you from previous visits, the data possibly could be used to identify you personally, even if you are not signed in to our website.
            </p>

            <h2>16. Our use of re-marketing</h2>
            <p>
              Re-marketing involves placing a cookie on your computer when you browse our website in order to be able to serve to you an advert for our products or services when you visit some other website.
            </p>
            <p>
              We may use a third party to provide us with re-marketing services from time to time. If so, then if you have consented to our use of cookies, you may see advertisements for our products and services on other websites.
            </p>

            <h2>17. Information we obtain from third parties</h2>
            <p>
              Although we do not disclose your personal information to any third party (except as set out in this notice), we sometimes receive data that is indirectly made up from your personal information from third parties whose services we use.
            </p>
            <p>
              No such information is personally identifiable to you.
            </p>

            <h2>18. Third party advertising on our website</h2>
            <p>
              Third parties may advertise on our website. In doing so, those parties, their agents or other companies working for them may use technology that automatically collects information about you when their advertisement is displayed on our website.
            </p>
            <p>
              They may also use other technology such as cookies or JavaScript to personalise the content of, and to measure the performance of their adverts.
            </p>
            <p>
              We do not have control over these technologies or the data that these parties obtain. Accordingly, this privacy policy does not cover the information practices of these third parties.
            </p>

            <h2>19. Data may be processed outside the European Union</h2>
            <p>
              Our websites are hosted in the United Kingdom.
            </p>
            <p>
              We may also use outsourced services in countries outside the European Union from time to time in other aspects of our business.
            </p>
            <p>
              Accordingly data obtained within the UK or any other country could be processed outside the European Union.
            </p>
            <p>
              We use the following safeguards with respect to data transferred outside the European Union:
            </p>
            <ul>
              <li>the processor is within the same corporate group as our business or organisation and abides by the same binding corporate rules regarding data processing.</li>
              <li>the data protection clauses in our contracts with data processors include transfer clauses written by or approved by a supervisory authority in the European Union.</li>
              <li>we comply with a code of conduct approved by a supervisory authority in the European Union.</li>
              <li>we are certified under an approved certification mechanism as provided for in the Act.</li>
              <li>both our organisation and the processor are public authorities between whom there is either a legally binding agreement or administrative arrangements approved by a supervisory authority in the European Union relating to protection of your information.</li>
            </ul>

            <h2>20. Your duty to inform us of changes</h2>
            <p>
              It is important that the personal data we hold about you is accurate and current. Please keep us informed if your personal data changes.
            </p>

            <h2>21. Access to your personal information</h2>
            <p>
              At any time you may review or update personally identifiable information that we hold about you, by signing in to your account on our website.
            </p>
            <p>
              To obtain a copy of any information that is not provided on our website you should contact us to make that request.
            </p>
            <p>
              After receiving the request, we will tell you when we expect to provide you with the information, and whether we require any fee for providing it to you.
            </p>

            <h2>22. Removal of your information</h2>
            <p>
              If you wish us to remove personally identifiable information from our website, you should contact us to make your request.
            </p>
            <p>
              This may limit the service we can provide to you.
            </p>

            <h2>23. Verification of your information</h2>
            <p>
              When we receive any request to access, edit or delete personal identifiable information we shall first take reasonable steps to verify your identity before granting you access or otherwise taking any action. This is important to safeguard your information.
            </p>

            <h2>24. Use of site by children</h2>
            <p>
              We do not sell products or provide services for purchase by children, nor do we market to children.
            </p>
            <p>
              If you are under 18, you may use our website only with consent from a parent or guardian
            </p>
            <p>
              We collect data about all users of and visitors to these areas regardless of age, and we anticipate that some of those users and visitors will be children.
            </p>
            <p>
              Such child users and visitors will inevitably visit other parts of the site and will be subject to whatever on-site marketing they find, wherever they visit.
            </p>

            <h2>25. Encryption of data sent between us</h2>
            <p>
              We use Secure Sockets Layer (SSL) certificates to verify our identity to your browser and to encrypt any data you give us.
            </p>
            <p>
              Whenever information is transferred between us, you can check that it is done so using SSL by looking for a closed padlock symbol or other trust mark in your browser's URL bar or toolbar.
            </p>

            <h2>26. How you can complain</h2>
            <p>
              If you are not happy with our privacy policy or if you have any complaint then you should tell us by emailing us at <a href="mailto:info@clariti.ai">info@clariti.ai</a>.
            </p>
            <p>
              If a dispute is not settled then we hope you will agree to attempt to resolve it by engaging in good faith with us in a process of mediation or arbitration.
            </p>
            <p>
              If you are in any way dissatisfied about how we process your personal information, you have a right to lodge a complaint with the Information Commissioner's Office (ICO). This can be done at <a href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noopener noreferrer">https://ico.org.uk/make-a-complaint/</a>. We would, however, appreciate the opportunity to talk to you about your concern before you approach the ICO.
            </p>

            <h2>27. Retention period for personal data</h2>
            <p>
              Except as otherwise mentioned in this privacy policy, we keep your personal information only for as long as required by us:
            </p>
            <ul>
              <li>to provide you with the services you have requested;</li>
              <li>to comply with other law, including for the period demanded by our tax authorities;</li>
              <li>to support a claim or defence in court.</li>
            </ul>

            <h2>28. Compliance with the law</h2>
            <p>
              Our privacy policy has been compiled so as to comply with the law of every country or legal jurisdiction in which we aim to do business. If you think it fails to satisfy the law of your jurisdiction, we should like to hear from you.
            </p>
            <p>
              However, ultimately it is your choice as to whether you wish to use our website.
            </p>

            <h2>29. Review of this privacy policy</h2>
            <p>
              We may update this privacy policy from time to time as necessary. The terms that apply to you are those posted here on our website on the day you use our website. We advise you to print a copy for your records.
            </p>
            <p>
              If you have any question regarding our privacy policy, please contact us on <a href="mailto:info@clariti.ai">info@clariti.ai</a>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Privacy;

